import React, { useEffect, useRef } from 'react';

/** How long an Undo is offered after a delete. */
export const UNDO_WINDOW_MS = 20000;

/**
 * The toast shown after a delete: "Entry deleted" with an Undo button, a restoring state
 * while the restore runs, and the reason when it fails.
 *
 * The row that held focus has unmounted by the time this appears, so the Undo button takes
 * focus on mount. `role="status"` announces it politely. An offer dismisses itself after
 * `durationMs`; the other states wait for the user.
 *
 * The caller keys the toast by the deleted entry's id, so a second delete mounts a fresh one:
 * a new countdown and focus on its own Undo, not the first delete's deadline. While
 * `disabled` (another write holds the page) the countdown does not run, so an offer cannot
 * expire while Undo cannot be pressed; its full window starts again once it can.
 *
 * @param {object} props
 * @param {{kind: 'offer'|'restoring'|'error', what?: string, message?: string}} props.state
 * @param {() => void} props.onUndo
 * @param {() => void} props.onDismiss
 * @param {boolean} [props.disabled] Another write on the page is in flight: Undo is a write
 *   too, and the page lets one run at a time.
 * @param {number} [props.durationMs]
 */
export default function EffortUndoToast({ state, onUndo, onDismiss, disabled = false, durationMs = UNDO_WINDOW_MS }) {
  const undoRef = useRef(null);
  const dismissRef = useRef(null);
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;
  const kind = state?.kind;

  useEffect(() => {
    if (kind === 'offer') undoRef.current?.focus();
    if (kind === 'error') dismissRef.current?.focus();
  }, [kind]);

  useEffect(() => {
    if (kind !== 'offer') return undefined;
    if (disabled) return undefined;
    const timer = setTimeout(() => dismiss.current(), durationMs);
    return () => clearTimeout(timer);
  }, [kind, durationMs, disabled]);

  if (!state) return null;
  const label = state.what?.trim() ? `"${state.what.trim()}"` : 'entry';

  return (
    <div
      role="status"
      data-testid="undo-toast"
      className="fixed bottom-6 left-1/2 z-50 flex max-w-[90vw] -translate-x-1/2 items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-gray-900 shadow-lg dark:border-gray-700 dark:bg-gray-900 dark:text-white"
    >
      {kind === 'error' ? (
        <>
          <p className="text-sm text-red-600 dark:text-red-400" data-testid="undo-error">{state.message}</p>
          <button
            ref={dismissRef}
            type="button"
            onClick={onDismiss}
            data-testid="undo-dismiss"
            className="text-sm font-medium text-[#32B1FF] hover:underline"
          >
            Dismiss
          </button>
        </>
      ) : (
        <>
          <p className="truncate text-sm">{kind === 'restoring' ? 'Restoring…' : `Deleted ${label}`}</p>
          <button
            ref={undoRef}
            type="button"
            onClick={onUndo}
            disabled={disabled || kind === 'restoring'}
            data-testid="undo-entry"
            className="text-sm font-medium text-[#32B1FF] hover:underline disabled:opacity-50 disabled:no-underline"
          >
            Undo
          </button>
        </>
      )}
    </div>
  );
}
